import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useLayout } from '../contexts/LayoutContext';
import { roleLabel } from '../utils/roles';
import { ehRotaAndonTv } from '../utils/andon';
import { IDIOMAS, bandeiraDoIdioma, trocarIdioma } from '../utils/idioma';
import StatusLink from './StatusLink';
import PlayAtivo from './PlayAtivo';
import PainelAcessibilidade from './PainelAcessibilidade';
import { NAMESPACE as NS_ACESSIBILIDADE } from '../acessibilidadePainel/i18n';
import MenuAvatar from './MenuAvatar';
import '../styles/Navbar.css';

const Navbar = () => {
  const location = useLocation();
  const isPublicBibliotecaPage = location.pathname.startsWith('/biblioteca/publica/');
  const isPublicCloudPage = location.pathname.startsWith('/cloud/publico/');
  const isPublicArPage = location.pathname.startsWith('/ar/');
  const { usuario, logout } = useAuth();
  const [showLanguages, setShowLanguages] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAcessibilidade, setShowAcessibilidade] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  const { sidebarMounted, toggleMobileSidebar, mobileSidebarOpen } = useLayout();
  const dropdownRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const { t, i18n } = useTranslation(['common', 'navbar', NS_ACESSIBILIDADE]);
  const [selectedLang, setSelectedLang] = useState((i18n.language || 'pt').slice(0, 2));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLanguages(false);
        setShowSettings(false);
      }
    };

    if (showLanguages || showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguages, showSettings]);

  // Garante sync da sigla (pt-BR -> pt)
  useEffect(() => {
    const lang = (i18n.language || 'pt').slice(0, 2);
    setSelectedLang(lang);
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [i18n.language, i18n]);

  const isPublicSupportPage = !usuario && location.pathname.startsWith('/suporte');
  // Login e cadastro deixaram de esconder a navbar: as telas de acesso usam o
  // mesmo cabeçalho e rodapé do resto do site, como já era em /recuperar-senha.
  // Andon modo TV: quiosque em tela cheia, sem topo.
  if (isPublicBibliotecaPage || isPublicCloudPage || isPublicArPage || isPublicSupportPage || ehRotaAndonTv(location.pathname)) return null;

  // O badge só existe para quem acumula papéis: mostra em qual perfil a sessão
  // está, já que a troca exige sair e entrar de novo.
  const papeisDoUsuario = usuario?.papeis || [];
  const papelAtivo = usuario?.papel_ativo || usuario?.tipo_usuario;
  const mostrarPapelAtivo = papeisDoUsuario.length > 1 && !!papelAtivo;

  const handleLanguageChange = (lang) => {
    setSelectedLang(trocarIdioma(i18n, lang));
    setShowLanguages(false);
  };

  const flagSrc = bandeiraDoIdioma(selectedLang);

  return (
    <header className="header">
      <div className="header-left">
        {sidebarMounted && usuario && (
          <button
            type="button"
            className="navbar-hamburger"
            onClick={toggleMobileSidebar}
            aria-label={t('navbar:menu', 'Menu')}
            aria-expanded={mobileSidebarOpen}
            aria-controls="app-sidebar"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}
        <img
          src={isDarkMode ? '/images/Exxer_Branca.png' : '/images/Exxer_roxa.png'}
          alt="Exxer"
          className="logo-img"
        />
      </div>

      <div className="user-area">
        {usuario && <PlayAtivo usuario={usuario} />}
        {usuario && (
          <div className="user-info" ref={dropdownRef}>
            <span className="user-name">{t('common:welcomeUser', { name: usuario.nome })}</span>

            {mostrarPapelAtivo && (
              <span
                className="user-role-badge"
                title={t('navbar:profileSwitchHint')}
              >
                {roleLabel(papelAtivo, t)}
              </span>
            )}

            <MenuAvatar
              usuario={usuario}
              onAbrir={() => {
                setShowSettings(false);
                setShowLanguages(false);
              }}
            />

            <button
              ref={settingsButtonRef}
              type="button"
              className="user-action settings-icon"
              onClick={() => {
                setShowSettings((prev) => !prev);
                setShowLanguages(false);
              }}
              title={t('navbar:settings')}
              aria-label={t('navbar:settings')}
              aria-expanded={showSettings}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.35" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21h-4v-.08A1.7 1.7 0 0 0 8.94 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.57 15 1.7 1.7 0 0 0 3 14v-4h.08A1.7 1.7 0 0 0 4.6 8.94a1.7 1.7 0 0 0-.34-1.88L4.2 7l2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.57 1.7 1.7 0 0 0 10 3h4v.08A1.7 1.7 0 0 0 15.06 4.6a1.7 1.7 0 0 0 1.88-.34L17 4.2 19.83 7l-.06.06a1.7 1.7 0 0 0-.34 1.88A1.7 1.7 0 0 0 21 10h.08v4H21a1.7 1.7 0 0 0-1.6 1Z" />
              </svg>
            </button>

            <button
              type="button"
              className="user-action language-selector"
              title={t('common:language')}
              aria-label={t('common:language')}
              aria-expanded={showLanguages}
              onClick={() => {
                setShowLanguages((prev) => !prev);
                setShowSettings(false);
              }}
            >
              <img
                src={flagSrc}
                alt=""
                className="flag"
              />
            </button>
            
            {showLanguages && (
              <div className="dropdown-menu">
                {IDIOMAS.map((idioma) => (
                  <button key={idioma.code} type="button" lang={idioma.code} onClick={() => handleLanguageChange(idioma.code)}>{idioma.name}</button>
                ))}
              </div>
            )}

            {showSettings && (
              <div className="dropdown-menu">
                <button type="button" onClick={toggleTheme}>
                  {isDarkMode ? '☀️ Modo claro' : '🌙 Modo escuro'}
                </button>
                <button
                  type="button"
                  className="a11y-menu-item"
                  aria-haspopup="dialog"
                  onClick={() => {
                    setShowSettings(false);
                    setShowAcessibilidade(true);
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="4.5" r="1.8" />
                    <path d="M5 8.5c2.3.7 4.6 1 7 1s4.7-.3 7-1" />
                    <path d="M12 9.5v4.5" />
                    <path d="M12 14l-3 6.5" />
                    <path d="M12 14l3 6.5" />
                  </svg>
                  {t(`${NS_ACESSIBILIDADE}:menuItem`)}
                </button>
                <StatusLink className="dropdown-status-link" />
                <hr className="dropdown-divider" />
                <button type="button" onClick={logout}>{t('common:logout')}</button>
              </div>
            )}
          </div>
        )}
      </div>
      <PainelAcessibilidade
        aberto={showAcessibilidade}
        aoFechar={() => {
          setShowAcessibilidade(false);
          // O item do menu que abriu o painel já não existe: o foco volta para a engrenagem.
          window.setTimeout(() => settingsButtonRef.current?.focus(), 0);
        }}
      />
    </header>
  );
};

export default Navbar;
